/**
 * useLiveReportDetailsData — fetch the live report by hash + side effects after load.
 *
 * Owns: apiRequest for getLiveReport / school subjects / referral preload, local raw payload,
 * parent-mode log. Mapping lives in the view-model; close/scroll nav in useLiveReportDetailsState.
 */
import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { apiIdle, apiRequest } from "../../../../../../data/redux/actions/api"
import { liveReportService } from "../../../../../../data/services/liveReportService"
import { referralService } from "../../../../../../data/services/referralService"
import { schoolSubjectsService } from "../../../../../../data/services/schoolSubjectsService"
import { subscriptionService } from "../../../../../../data/services/subscriptionService"
import { hasRole } from "../../../../../../data/utility/ability"
import { type ApiLiveReportDetails } from "../../../../adapters/parent/LiveReports/liveReportDetailsAdapter"
import { type ReferralCodePayload } from "../../../types/referralCode"
import { selectAccount } from "../../../../view-models/shared/displayedStudent"

type ApiSlice = {
    request?: boolean
    success?: boolean
    error?: unknown
    data?: ApiLiveReportDetails
}

type ReferralSlice = {
    request?: boolean
    success?: boolean
    error?: unknown
    data?: ReferralCodePayload
}

export function useLiveReportDetailsData(hash: string | null | undefined) {
    const dispatch = useDispatch()
    const [raw, setRaw] = useState<ApiLiveReportDetails | null>(null)
    const [preloadedReferralCode, setPreloadedReferralCode] = useState<ReferralCodePayload | null>(null)
    /** Avoid re-running side effects if the same success payload is observed twice. */
    const handledReportSuccessRef = useRef(false)
    const handledReferralSuccessRef = useRef(false)

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const currentUser = useSelector((state: any) => selectAccount(state))
    const isAdminOrTutor = !!(hasRole(currentUser, "admin") || hasRole(currentUser, "tutor"))

    const reportReq = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => state?.api?.getLiveReport as ApiSlice | undefined
    )
    const referralReq = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => state?.api?.getReferralCode as ReferralSlice | undefined
    )

    const userEmail = raw?.student?.user?.email || raw?.student?.email || null
    const engagementScore = raw?.statistics?.scores?.total_score

    const fetchReport = useCallback(() => {
        if (!hash) return
        handledReportSuccessRef.current = false
        handledReferralSuccessRef.current = false
        setRaw(null)
        setPreloadedReferralCode(null)
        dispatch(apiRequest(liveReportService.getLiveReport, { hash }, {}, { with_linked_assessments: 1 }) as never)
        dispatch(apiRequest(schoolSubjectsService.getSchoolSubjects) as never)
    }, [dispatch, hash])

    useEffect(() => {
        fetchReport()
    }, [fetchReport])

    useEffect(() => {
        if (!reportReq?.success || handledReportSuccessRef.current) return
        handledReportSuccessRef.current = true
        const successData = reportReq.data ?? null
        setRaw(successData)
        dispatch(apiIdle(liveReportService.getLiveReport) as never)

        if (!isAdminOrTutor && successData) {
            const email = successData?.student?.user?.email || successData?.student?.email
            const params = email ? { email } : {}
            dispatch(
                apiRequest(subscriptionService.logParentModeAction, {}, { action: "live_report_view" }, params) as never
            )
            // Preload referral code for the share modal (legacy parity).
            handledReferralSuccessRef.current = false
            dispatch(apiRequest(referralService.getReferralCode, {}, {}, params) as never)
        }
    }, [dispatch, isAdminOrTutor, reportReq?.success, reportReq?.data])

    useEffect(() => {
        if (!referralReq?.success || handledReferralSuccessRef.current) return
        handledReferralSuccessRef.current = true
        setPreloadedReferralCode(referralReq.data ?? null)
        dispatch(apiIdle(referralService.getReferralCode) as never)
    }, [dispatch, referralReq?.success, referralReq?.data])

    useEffect(() => {
        return () => {
            dispatch(apiIdle(liveReportService.getLiveReport) as never)
            dispatch(apiIdle(referralService.getReferralCode) as never)
        }
    }, [dispatch, hash])

    // Prefer local raw over the api slice for loading — after success we idle before the
    // next paint applies `setRaw`, and `request` alone would briefly look "done" with no report.
    const loading = hash != null && raw == null && reportReq?.error == null

    return {
        raw,
        loading,
        retry: fetchReport,
        userEmail,
        engagementScore,
        isAdminOrTutor,
        preloadedReferralCode,
    }
}
